import SearchDropdown, { type Option } from '@allesauto/core/searchDropdown';
import translate from '@allesauto/i18n';
import {
  useGoogleMapsGeocode,
  useGoogleMapsPlaceAutocomplete,
} from '@allesauto/utils/apiHooks';
import { consts } from '@allesauto/utils/consts';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

export type LocationFilterValue = {
  location?: Option;
  latitude?: number;
  longitude?: number;
  radius?: number;
};

export interface LocationFilterProps {
  location?: Option;
  radius: number;
  onLocationFilterChange?: (value?: LocationFilterValue) => void;
}

const LocationFilter = ({
  location,
  radius,
  onLocationFilterChange = () => null,
}: LocationFilterProps) => {
  const intl = useIntl();
  const didMount = useRef(false);

  const countryBoundary = useSelector(
    (state: any) => state.app.googleMaps.countryBoundary
  );

  const [inputValue, setInputValue] = useState('');

  const onLocationInputChange = useCallback(
    debounce((v) => setInputValue(v), 100),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const MIN_AUTOCOMPLETE_LENGTH = 3;

  const { results: places, loading: placesLoading } =
    useGoogleMapsPlaceAutocomplete({
      input: inputValue,
      language: intl.locale || intl.defaultLocale,
      components: countryBoundary,
      minLength: MIN_AUTOCOMPLETE_LENGTH,
    });

  const { result: latLng } = useGoogleMapsGeocode({
    placeId: location?.value as string,
  });

  const trackingIdentifierLocation = 'aa-filter--location';
  const trackingIdentifierRadius = 'aa-filter--radius';

  useEffect(() => {
    if (latLng && didMount.current) {
      onLocationFilterChange({
        location: location as Option,
        latitude: (latLng.lat && parseFloat(latLng.lat)) || 0,
        longitude: (latLng.lon && parseFloat(latLng.lon)) || 0,
      });
    } else {
      didMount.current = true;
    }
  }, [onLocationFilterChange, latLng, location]);

  const radiusOptions = useMemo(() => {
    return consts.PCP.FILTERS.LOCATION_RADIUS_OPTIONS.map((r) => ({
      value: r,
      label: r,
    }));
  }, []);

  const radiusValue = useMemo(() => {
    return radiusOptions.find((r) => r.value === radius) ?? radiusOptions[0];
  }, [radiusOptions, radius]);

  return (
    <>
      <label
        htmlFor={`${trackingIdentifierLocation}--input`}
        className="block font-weight-normal pb-10px pt-0 text-p1 text-white"
      >
        {translate(intl, 'ui.PCP.FILTERS.LOCATION.LOCATION_HEADLINE')}
      </label>
      <div className="pb-20px">
        <SearchDropdown
          id={trackingIdentifierLocation}
          onInputChange={onLocationInputChange}
          options={places.map((p) => ({
            value: p.place_id,
            label: p.description,
          }))}
          value={location ?? null}
          onChange={(value) => {
            if (value) {
              onLocationFilterChange({
                location: value as Option,
                latitude: undefined,
                longitude: undefined,
              });
            } else {
              onLocationFilterChange(); // x was clicked, reset the whole filter
            }
          }}
          hideDropdownIndicator
          darkBg
          placeholder={translate(
            intl,
            'ui.PCP.FILTERS.LOCATION.LOCATION_PLACEHOLDER'
          )}
          context="relative-position"
          hideNoOptions={
            inputValue.length < MIN_AUTOCOMPLETE_LENGTH || placesLoading
          }
        />
      </div>

      <label
        htmlFor={`${trackingIdentifierRadius}--input`}
        className="block font-weight-normal pb-10px pt-0 text-p1 text-white"
      >
        {translate(intl, 'ui.PCP.FILTERS.LOCATION.RADIUS_LABEL')}
      </label>

      <SearchDropdown
        id={trackingIdentifierRadius}
        options={radiusOptions}
        value={radiusValue}
        onChange={(option) =>
          onLocationFilterChange({
            radius: (option as Option).value as number,
          })
        }
        isSearchable={false}
        isClearable={false}
        disabled={!location}
        context="relative-position"
        darkBg
      />
    </>
  );
};

export default LocationFilter;
